/**
 * Sem os dados 3D (docs/spec/dice-3d.md, "Sem animação"): por que o navegador não desenha 3D e os
 * passos para ligar a aceleração gráfica em cada navegador. O site não lê as configurações do
 * navegador; quem conta o motivo é a mensagem do evento `webglcontextcreationerror`.
 */

/**
 * Por que não há WebGL:
 * - `gpu-off`: o navegador está sem a placa de vídeo (a aceleração gráfica desligada, ou desligada
 *   por ele depois de uma falha). Ligar a aceleração e reabrir o navegador resolve;
 * - `policy`: o WebGL foi desligado por uma política (empresa, escola) ou por uma opção ao abrir o
 *   navegador. Ligar a aceleração não resolve;
 * - `unknown`: outra mensagem, ou nenhuma (o Firefox e o Safari quase não explicam).
 */
export type No3DReason = 'gpu-off' | 'policy' | 'unknown';

/** O pedaço das chaves do i18n de cada motivo (`dice.no3d.why.gpuOff`, `dice.no3d.toast.gpuOff`…). */
export const NO3D_REASON_KEY: Record<No3DReason, string> = { 'gpu-off': 'gpuOff', policy: 'policy', unknown: 'unknown' };

export function no3dReason(message: string | null | undefined): No3DReason {
  if (!message) return 'unknown';
  // o Chromium sem a placa: "VENDOR = 0xffff, DEVICE = 0xffff, GL_VENDOR = Disabled, GL_RENDERER = Disabled"
  if (/GL_VENDOR = Disabled|VENDOR = 0xffff/i.test(message)) return 'gpu-off';
  // o Chromium com o WebGL desligado: "disabled by enterprise policy or commandline switch"
  if (/enterprise policy|commandline switch|command line/i.test(message)) return 'policy';
  return 'unknown';
}

/**
 * O navegador, para os passos: o Opera tem atalho próprio no Windows; o Firefox e o Safari têm outros
 * caminhos; o resto (Chrome, Edge, Brave, Vivaldi e os que não se reconhece) usa o do Chromium.
 */
export type BrowserFamily = 'opera' | 'chromium' | 'firefox' | 'safari';

export function browserFamily(ua: string): BrowserFamily {
  if (/\bOPR\//.test(ua)) return 'opera';
  if (/\b(?:Firefox|FxiOS)\//.test(ua)) return 'firefox';
  if (/\b(?:Chrome|Chromium|CriOS|Edg)\//.test(ua)) return 'chromium';
  if (/\bVersion\/[\d.]+.*\bSafari\//.test(ua)) return 'safari';
  return 'chromium';
}

/** É um Mac (o iPhone também diz "like Mac OS X", mas não "Macintosh")? */
export function isMac(ua: string): boolean {
  return /\bMacintosh\b/.test(ua);
}

/** Um passo: a chave do texto e os parâmetros; `option` é a chave do nome da opção, que vai em negrito no `{option}`. */
export type No3DStep = { key: string; params?: Record<string, string>; option?: string };

/** Os passos para ligar a aceleração gráfica no navegador desta pessoa. */
export function no3dSteps(family: BrowserFamily, mac: boolean): No3DStep[] {
  if (family === 'safari') return [{ key: 'dice.no3d.step.safari' }];
  const shortcut = mac ? '⌘ + ,' : family === 'opera' ? 'Alt + P' : null;
  const open: No3DStep = shortcut ? { key: 'dice.no3d.step.openKeys', params: { keys: shortcut } } : { key: 'dice.no3d.step.open' };
  // a opção só vale com o navegador reaberto; se ele desligou a placa depois de uma falha, também
  const quit: No3DStep = mac ? { key: 'dice.no3d.step.quitKeys', params: { keys: '⌘ + Q' } } : { key: 'dice.no3d.step.quit' };
  if (family === 'firefox') {
    return [
      open,
      { key: 'dice.no3d.step.searchPerformance' },
      { key: 'dice.no3d.step.uncheck', option: 'dice.no3d.option.firefoxRecommended' },
      { key: 'dice.no3d.step.check', option: 'dice.no3d.option.firefoxHardware' },
      quit,
    ];
  }
  return [open, { key: 'dice.no3d.step.searchAcceleration' }, { key: 'dice.no3d.step.turnOn', option: 'dice.no3d.option.graphics' }, quit];
}
